import React, { useEffect, useState } from 'react';
import { FormLayoutComposer, IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { burstAPI, instance } from 'api/api';
import { ActSubmit } from 'components/common/form/act';
import useBundleTranslation from 'i18n';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { APIResponse } from 'tools/types';
import { AxiosResponse } from 'axios';
import { FormRendererAPIType } from 'components/common/form';
import { useFormState } from 'react-hook-form';

function useTooltip(form: FormRendererAPIType) {
    const urlParams = useParams();
    const { t } = useBundleTranslation('app/notification/burst');

    const datasetId = form.hookFormWatch('parameter_substitution_dataset_id');
    const burstId = urlParams.burstId;

    const queryKey = 'fields_tooltip';
    const { data: tooltipData, refetch } = useQuery<string, Error>({
        queryKey: [queryKey],
        queryFn: async () => {
            if (!datasetId || datasetId == '0' || !burstId) {
                return '';
            }
            const response: AxiosResponse<APIResponse<{ params: string }>> = await instance.get(
                `data/notification/burst/${burstId}/dataset/${datasetId}/substitution-parameter/get`,
            );
            return response.data.status == 'OK' ? response.data.data.params : '';
        },
    });

    const gridData = form.hookFormWatch('burstSubstitutionParametersGrid');
    useEffect(() => {
        refetch();
    }, [gridData]);

    const updateTooltip = () => {
        let tooltip = t('fields_tooltip');
        if (tooltipData && tooltipData.length > 0) {
            tooltip += ', ' + tooltipData;
        }
        form.hookFormSetValue('fake_fields_tooltip', tooltip);
    };

    useEffect(() => {
        updateTooltip();
    }, [tooltipData]);
}

function useShowSaveChangesUM(form: FormRendererAPIType) {
    const checkBox = form.hookFormWatch('use_subscription_user_map_ind');
    const { dirtyFields } = useFormState({ control: form.hookFormControl });
    const userMapWasChanged = Object.keys(dirtyFields).includes('subscription_user_map_id');
    useEffect(() => {
        form.hookFormSetValue('show_save_changes_for_um', checkBox == 'Y' && userMapWasChanged ? 'Y' : 'N');
    }, [checkBox, userMapWasChanged]);
}

export default function BurstForm(props: IFormLayoutProps) {
    const nsDistributionId = props.hookForm.form.hookFormWatch('notification_schedule_distribution_id');
    const contentType = props.hookForm.form.hookFormWatch('content_type');
    const unsubscribedUsersGrid = props.hookForm.form.hookFormWatch('burstRecipientUnsubscribedUsersGrid');

    const [prevUnsubscribedUsersGrid, setPrevUnsubscribedUsersGrid] = useState([]);

    useTooltip(props.hookForm.form);
    useShowSaveChangesUM(props.hookForm.form);
    const syncEnableState = () => {
        burstAPI
            .getEnabledState(nsDistributionId)
            .then((response) => {
                if ('OK' == response.data?.status) {
                    props.hookForm.form.hookFormSetValue('enabled_ind', response.data.data);
                }
            })
            .catch(() => {});
    };

    useEffect(() => {
        syncEnableState();
    }, [contentType]);

    const setCurrentNS = () => {
        props.hookForm.form.hookFormSetValue(
            'burst_status_ns',
            props.hookForm.form.hookFormGetValues('notification_schedule_id'),
        );
    };

    useEffect(() => {
        if (!props.hookForm.form.formDidMount) {
            return;
        }
        setCurrentNS();
    }, [props.hookForm.form.formDidMount]);

    useEffect(() => {
        if (unsubscribedUsersGrid?.length === 0 && prevUnsubscribedUsersGrid?.length > 0) {
            props.hookForm.form.hookFormSetValue('send_owner_distribution_ind', 'Y');
        }

        if (unsubscribedUsersGrid) {
            setPrevUnsubscribedUsersGrid(unsubscribedUsersGrid);
        }
    }, [unsubscribedUsersGrid]);

    props.hookForm.form.formAct.overrideAct(
        'submit',
        class extends ActSubmit {
            async exec(data: any): Promise<any> {
                return super.exec(data).then((response) => {
                    syncEnableState();
                    setCurrentNS();
                    return response;
                });
            }
        },
    );

    return <FormLayoutComposer props={props} />;
}
